import React from "react";
import { Card, Row, Col, Typography, Empty, Statistic } from "antd";
import {
  MobileOutlined,
  DesktopOutlined,
  TabletOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { motion } from "framer-motion";

const { Text } = Typography;

const DEVICE_COLORS = {
  desktop: "#1890ff",
  mobile: "#52c41a",
  tablet: "#722ed1",
  other: "#8c8c8c",
};

const BROWSER_COLORS = ["#1890ff", "#52c41a", "#722ed1", "#fa8c16", "#13c2c2", "#eb2f96"];

const DeviceBreakdown = ({
  title = "Device Analytics",
  data = {},
  loading = false,
  style = {},
}) => {
  const {
    devices = {},
    browsers = {},
    operatingSystems = {},
  } = data;

  // Transform device data for pie chart
  const deviceData = Object.entries(devices).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value,
    color: DEVICE_COLORS[name.toLowerCase()] || DEVICE_COLORS.other,
  }));

  // Transform browser data for bar chart
  const browserData = Object.entries(browsers)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 6);

  // Transform OS data for display
  const osData = Object.entries(operatingSystems)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  // Calculate totals
  const totalDevices = deviceData.reduce((sum, item) => sum + item.value, 0);
  const totalBrowsers = browserData.reduce((sum, item) => sum + item.value, 0);

  // Get device icon
  const getDeviceIcon = (device) => {
    const icons = {
      desktop: <DesktopOutlined />,
      mobile: <MobileOutlined />,
      tablet: <TabletOutlined />,
    };
    return icons[device.toLowerCase()] || <GlobalOutlined />;
  };

  // Custom tooltip for pie chart
  const CustomPieTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const data = payload[0];
    const percentage = ((data.value / totalDevices) * 100).toFixed(1);
    return (
      <div
        style={{
          background: "white",
          padding: "8px 12px",
          border: "1px solid #f0f0f0",
          borderRadius: 4,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Text strong>{data.name}</Text>
        <br />
        <Text type="secondary">
          {data.value.toLocaleString()} ({percentage}%)
        </Text>
      </div>
    );
  };

  if (
    Object.keys(devices).length === 0 &&
    Object.keys(browsers).length === 0 &&
    Object.keys(operatingSystems).length === 0
  ) {
    return (
      <Card title={title} bordered={false} loading={loading} style={style}>
        <Empty
          description="No device data available"
          style={{ padding: "40px 0" }}
        />
      </Card>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card title={title} bordered={false} loading={loading} style={style}>
        <Row gutter={[24, 24]}>
          {/* Device Distribution */}
          <Col xs={24} lg={12}>
            <Card
              title="Device Distribution"
              size="small"
              bordered={false}
              style={{ background: "#fafafa" }}
            >
              {deviceData.length > 0 ? (
                <>
                  <div style={{ height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={deviceData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={2}
                        >
                          {deviceData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomPieTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  {/* Legend */}
                  <Row gutter={8} justify="center" style={{ marginTop: 16 }}>
                    {deviceData.map((item, index) => (
                      <Col key={index}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "4px 12px",
                            background: `${item.color}15`,
                            borderRadius: 16,
                            marginBottom: 8,
                          }}
                        >
                          <span
                            style={{
                              color: item.color,
                              marginRight: 6,
                              fontSize: 16,
                            }}
                          >
                            {getDeviceIcon(item.name)}
                          </span>
                          <Text style={{ fontSize: 12 }}>
                            {item.name}: {((item.value / totalDevices) * 100).toFixed(1)}%
                          </Text>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </>
              ) : (
                <Empty description="No device data" />
              )}
            </Card>
          </Col>

          {/* Browser Distribution */}
          <Col xs={24} lg={12}>
            <Card
              title="Browser Usage"
              size="small"
              bordered={false}
              style={{ background: "#fafafa" }}
            >
              {browserData.length > 0 ? (
                <div style={{ height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={browserData}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 60, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" />
                      <YAxis
                        dataKey="name"
                        type="category"
                        tick={{ fontSize: 12 }}
                        width={80}
                      />
                      <Tooltip
                        formatter={(value) => [value.toLocaleString(), "Users"]}
                      />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={20}>
                        {browserData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={BROWSER_COLORS[index % BROWSER_COLORS.length]}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <Empty description="No browser data" />
              )}
            </Card>
          </Col>

          {/* Operating Systems */}
          <Col span={24}>
            <Card
              title="Operating Systems"
              size="small"
              bordered={false}
              style={{ background: "#fafafa" }}
            >
              {osData.length > 0 ? (
                <Row gutter={[16, 16]}>
                  {osData.slice(0, 6).map((os, index) => (
                    <Col xs={12} sm={8} md={4} key={index}>
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                      >
                        <Card
                          size="small"
                          style={{
                            textAlign: "center",
                            background: `${BROWSER_COLORS[index % BROWSER_COLORS.length]}10`,
                            borderLeft: `3px solid ${BROWSER_COLORS[index % BROWSER_COLORS.length]}`,
                          }}
                        >
                          <Statistic
                            title={
                              <Text
                                style={{ fontSize: 11 }}
                                ellipsis={{ tooltip: os.name }}
                              >
                                {os.name.length > 12
                                  ? `${os.name.substring(0, 12)}...`
                                  : os.name}
                              </Text>
                            }
                            value={os.value}
                            valueStyle={{
                              fontSize: 16,
                              color: BROWSER_COLORS[index % BROWSER_COLORS.length],
                            }}
                          />
                        </Card>
                      </motion.div>
                    </Col>
                  ))}
                </Row>
              ) : (
                <Empty description="No OS data" />
              )}
            </Card>
          </Col>
        </Row>
      </Card>
    </motion.div>
  );
};

export default DeviceBreakdown;
